import {
    type FavoritesSearchParams,
    FavoritesSearchSchema,
    PRIVATE_FAVORITE_TYPES,
    PUBLIC_FAVORITE_TYPES,
    type TrendingSearchParams,
    TrendingSearchSchema,
} from "@compass/shared/schema/favorite";
import { Navigate, Outlet, ReactRouter, RootRoute, Route, useRouter } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { z } from "zod";
import { Spinner } from "components/elements";
import { CompassEventsLayout } from "components/layout/CompassEventsLayout";
import { EventsLayout } from "components/layout/EventsLayout";
import { Page } from "components/layout/Page";
import { PortalLayout } from "components/layout/PortalLayout";
import { SocialLayout } from "components/layout/SocialLayout";
import { VendorLayout } from "components/layout/VendorLayout";
import { useTrackUserLogin } from "hooks/useAnalytics";
import { useIsUserLoggedIn } from "hooks/useIsUserLoggedIn";
import { redirectManager } from "lib/redirectManager";
import { trpc } from "lib/trpc";
// Auth screens — kept eager (small, needed before JS fully loads)
import { ForgotPassword } from "screens/ForgotPassword";
import { Login } from "screens/Login";
import { Logout } from "screens/Logout";
import { Masquerade } from "screens/Masquerade";
import { NotFound } from "screens/NotFound";
import { ResetPassword } from "screens/ResetPassword";
import {
    ChartsListSchema,
    ChartsList as ChartsListSchemaType,
    DiscussionsListSchema,
    DiscussionsList as DiscussionsListSchemaType,
    DocumentsListSchema,
    DocumentsList as DocumentsListSchemaType,
    VendorListSchema,
    VendorList as VendorListSchemaType,
} from "@compass/shared/types";

// Authenticated screens — lazy loaded (all render inside portalRoute's Suspense boundary)
const AuthorPosts = lazy(() =>
    import("screens/AuthorPosts").then((m) => ({ default: m.AuthorPosts })),
);
const Documents = lazy(() => import("screens/Documents").then((m) => ({ default: m.Documents })));
const ResourceCategory = lazy(() =>
    import("screens/ResourceCategory").then((m) => ({ default: m.ResourceCategory })),
);
const ResourcePost = lazy(() =>
    import("screens/ResourcePost").then((m) => ({ default: m.ResourcePost })),
);
const ChartDetailScreen = lazy(() =>
    import("screens/charts/ChartDetailScreen").then((m) => ({ default: m.ChartDetailScreen })),
);
const ChartsListScreen = lazy(() =>
    import("screens/charts/ChartsListScreen").then((m) => ({ default: m.ChartsListScreen })),
);
const CreateChartScreen = lazy(() =>
    import("screens/charts/CreateChartScreen").then((m) => ({ default: m.CreateChartScreen })),
);
const CompassEvents = lazy(() =>
    import("screens/compassEvents/CompassEvents").then((m) => ({ default: m.CompassEvents })),
);
const CompassEventJoin = lazy(() =>
    import("screens/compassEvents/CompassEventJoin").then((m) => ({ default: m.CompassEventJoin })),
);
const Meetings = lazy(() =>
    import("screens/meetings/Meetings").then((m) => ({ default: m.Meetings })),
);
const MeetingsLayout = lazy(() =>
    import("screens/meetings/MeetingsLayout").then((m) => ({ default: m.MeetingsLayout })),
);
const PreviousMeetings = lazy(() =>
    import("screens/meetings/PreviousMeetings").then((m) => ({ default: m.PreviousMeetings })),
);
const MeetingSeriesDetail = lazy(() =>
    import("screens/meetings/MeetingSeriesDetail").then((m) => ({
        default: m.MeetingSeriesDetail,
    })),
);
const MeetingInstanceDetail = lazy(() =>
    import("screens/meetings/MeetingInstanceDetail").then((m) => ({
        default: m.MeetingInstanceDetail,
    })),
);
const AIDigest = lazy(() =>
    import("screens/digest/AIDigest").then((m) => ({ default: m.AIDigest })),
);
const AIDigestDetail = lazy(() =>
    import("screens/digest/AIDigestDetail").then((m) => ({ default: m.AIDigestDetail })),
);
const DiscussionMessage = lazy(() =>
    import("screens/discussions/DiscussionMessage").then((m) => ({ default: m.DiscussionMessage })),
);
const DiscussionsList = lazy(() =>
    import("screens/discussions/List").then((m) => ({ default: m.DiscussionsList })),
);
const EventDetailPage = lazy(() =>
    import("screens/events").then((m) => ({ default: m.EventDetailPage })),
);
const EventJoin = lazy(() =>
    import("screens/events/EventJoin").then((m) => ({ default: m.EventJoin })),
);
const Events = lazy(() => import("screens/events/Events").then((m) => ({ default: m.Events })));
const Favorites = lazy(() =>
    import("screens/favorites/Favorites").then((m) => ({ default: m.Favorites })),
);
const FriendsTable = lazy(() => import("screens/friends/FriendsList"));
const Live = lazy(() => import("screens/live/Live").then((m) => ({ default: m.Live })));
const LiveJoin = lazy(() => import("screens/live/LiveJoin").then((m) => ({ default: m.LiveJoin })));
const Messages = lazy(() =>
    import("screens/messages/Messages").then((m) => ({ default: m.Messages })),
);
const NotificationsPage = lazy(() =>
    import("screens/notifications/NotificationsPage").then((m) => ({
        default: m.NotificationsPage,
    })),
);
const ResolveNotification = lazy(() =>
    import("screens/notifications/ResolveNotification").then((m) => ({
        default: m.ResolveNotification,
    })),
);
const EditProfile = lazy(() =>
    import("screens/profile/EditProfile").then((m) => ({ default: m.EditProfile })),
);
const Profile = lazy(() => import("screens/profile/Profile").then((m) => ({ default: m.Profile })));
const UserProfile = lazy(() =>
    import("screens/profile/UserProfile").then((m) => ({ default: m.UserProfile })),
);
const FullCategory = lazy(() =>
    import("screens/rss/FullCategory").then((m) => ({ default: m.FullCategory })),
);
const RssList = lazy(() => import("screens/rss/RssList").then((m) => ({ default: m.RssList })));
const RssArticle = lazy(() =>
    import("screens/rss/RssArticle").then((m) => ({ default: m.RssArticle })),
);
const SettingsNotifications = lazy(() =>
    import("screens/settings/SettingsNotifications").then((m) => ({
        default: m.SettingsNotifications,
    })),
);
const SettingsCalendars = lazy(() =>
    import("screens/settings/SettingsCalendars").then((m) => ({
        default: m.SettingsCalendars,
    })),
);
const SettingsSecurity = lazy(() =>
    import("screens/settings/SettingsSecurity").then((m) => ({ default: m.SettingsSecurity })),
);
const Social = lazy(() => import("screens/social/Social").then((m) => ({ default: m.Social })));
const SocialMain = lazy(() =>
    import("screens/social/SocialMain").then((m) => ({ default: m.SocialMain })),
);
const SocialRead = lazy(() =>
    import("screens/social/SocialRead").then((m) => ({ default: m.SocialRead })),
);
const Trending = lazy(() =>
    import("screens/trending/Trending").then((m) => ({ default: m.Trending })),
);
const BenchMark = lazy(() =>
    import("screens/vendors/BenchMark").then((m) => ({ default: m.BenchMark })),
);
const Budget = lazy(() => import("screens/vendors/Budget").then((m) => ({ default: m.Budget })));
const VendorDetail = lazy(() =>
    import("screens/vendors/VendorDetail").then((m) => ({ default: m.VendorDetail })),
);
const VendorList = lazy(() =>
    import("screens/vendors/VendorList").then((m) => ({ default: m.VendorList })),
);

const rootRoute = new RootRoute({
    component: function Root() {
        const isLoggedIn = useIsUserLoggedIn();
        const router = useRouter();

        const {
            data: user,
            isLoading: isUserLoading,
            isError: isUserError,
        } = trpc.users.me.useQuery(undefined, {
            enabled: isLoggedIn,
        });
        useTrackUserLogin(user?.email || null, user?.name || null);

        const currentPath = router.state.currentLocation.pathname;
        const isHomePage = ["/"].includes(currentPath);
        const isForgotPasswordPage = ["/forgot-password"].includes(currentPath);
        const isResetPasswordPage = currentPath.startsWith("/reset-password");
        const isLoginPage = currentPath === "/login";
        const isLogoutPage = currentPath === "/logout";
        // PRTL-1348: the masquerade exchange page authenticates ITSELF (via
        // the code in the URL fragment) — it must render for logged-out
        // visitors, and a logged-in admin must not be bounced off it either.
        const isMasqueradePage = currentPath === "/masquerade";

        // Token exists but query failed (expired/invalid token) — redirect to login
        if (isLoggedIn && isUserError) {
            return <Navigate to="/login" />;
        }

        if ((isLoginPage || isHomePage) && isLoggedIn) {
            // Check for a saved redirect from before login
            const savedPath = redirectManager.consume();
            if (savedPath) {
                return <Navigate to={savedPath as "/"} />;
            }
            if (user && user.isSocialEnabled) {
                return <Navigate to="/social" />;
            }
            if (user && user.isPrivateEnabled) {
                return <Navigate to="/discussions" />;
            }
            if (isUserLoading) {
                return (
                    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-[#e8f4f8] to-[#d4e9f0]">
                        <Spinner size="xl" />
                    </div>
                );
            }
        }

        if (
            !isLoginPage &&
            !isLogoutPage &&
            !isMasqueradePage &&
            !isLoggedIn &&
            !isForgotPasswordPage &&
            !isResetPasswordPage
        ) {
            // Save intended URL so we can restore it after login
            const fullPath =
                router.state.currentLocation.pathname + router.state.currentLocation.searchStr;
            redirectManager.save(fullPath);
            return <Navigate to="/login" />;
        }

        // if (isLoggedIn && user && isSocialPage && !user.isSocialEnabled) {
        // return <Navigate to="/" replace />;
        // return <div>Sorry, you don’t have access to this page.</div>;
        // }
        return <Page isLoggedIn={isLoggedIn} user={user} />;
    },
});

/**
 * Portal
 */

const portalRoute = new Route({
    getParentRoute: () => rootRoute,
    id: "portal",
    component: () => (
        <Suspense
            fallback={
                <div className="flex min-h-screen items-center justify-center">
                    <Spinner size="xl" />
                </div>
            }
        >
            <PortalLayout />
        </Suspense>
    ),
});

// Redirect "/" to "/social"
// const homeRoute = new Route({
//     getParentRoute: () => portalRoute,
//     path: "/",
//     component: function Home() {
//         const { data: user } = trpc.users.me.useQuery();
//
//         if (user?.isSocialEnabled) {
//             return <Navigate to="/social" replace />;
//         } else {
//             return <Navigate to="/documents" replace />;
//         }
//     },
// });

// Favorites layout route — just an Outlet, no component of its own
export const favoritesRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "favorites",
});

// /favorites (index) — no default types
export const favoritesIndexRoute = new Route({
    getParentRoute: () => favoritesRoute,
    path: "/",
    component: () => <Favorites />,
    validateSearch: (search: Record<string, unknown>): FavoritesSearchParams =>
        FavoritesSearchSchema.parse(search),
});

// /favorites/private — defaults to all private types, shows private nav
export const favoritesPrivateRoute = new Route({
    getParentRoute: () => favoritesRoute,
    path: "private",
    component: () => <Favorites />,
    validateSearch: (search: Record<string, unknown>): FavoritesSearchParams => {
        const parsed = FavoritesSearchSchema.parse(search);
        return { ...parsed, types: parsed.types ?? PRIVATE_FAVORITE_TYPES.join(",") };
    },
});

// /favorites/public — defaults to all public types, shows social nav
export const favoritesPublicRoute = new Route({
    getParentRoute: () => favoritesRoute,
    path: "public",
    component: () => <Favorites />,
    validateSearch: (search: Record<string, unknown>): FavoritesSearchParams => {
        const parsed = FavoritesSearchSchema.parse(search);
        return { ...parsed, types: parsed.types ?? PUBLIC_FAVORITE_TYPES.join(",") };
    },
});

export const trendingRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "trending",
    component: () => <Trending />,
    validateSearch: (search: Record<string, unknown>): TrendingSearchParams =>
        TrendingSearchSchema.parse(search),
});

export const resourceCategoryRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "/c/$slug",
    component: () => <ResourceCategory />,
});

export const resourcePostRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "/p/$slug",
    component: () => <ResourcePost />,
});

export const authorPostsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "/author/$id",
    component: () => <AuthorPosts />,
});

const discussionsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "discussions",
    component: () => <Outlet />,
});

export const discussionsList = new Route({
    getParentRoute: () => discussionsRoute,
    path: "/",
    component: () => <DiscussionsList />,
    validateSearch: (search: Record<string, unknown>): DiscussionsListSchemaType => {
        return DiscussionsListSchema.parse(search);
    },
});

// const discussionsNewRoute = new Route({
//     getParentRoute: () => discussionsRoute,
//     path: "/new",
//     component: () => <DiscussionsNew />,
// });

export const discussionsReadRoute = new Route({
    getParentRoute: () => discussionsRoute,
    path: "/$discussionId/$messageId",
    component: () => <DiscussionMessage />,
});

// export const discussionsReadMessageRoute = new Route({
//     getParentRoute: () => discussionsReadRoute,
//     path: "/message/$messageId",
//     component: () => <DiscussionsRead />,
// });

export const documentsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "documents",
    component: () => <Documents />,
    validateSearch: (search: Record<string, unknown>): DocumentsListSchemaType => {
        return DocumentsListSchema.parse(search);
    },
});

export const settingsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "settings",
    component: () => <Outlet />,
});

export const settingsSecurity = new Route({
    getParentRoute: () => settingsRoute,
    path: "/security",
    component: () => <SettingsSecurity />,
});

export const settingsNotifications = new Route({
    getParentRoute: () => settingsRoute,
    path: "/notifications",
    component: () => <SettingsNotifications />,
});

// PRTL-857: gated on calendarImports inside the screen, so the route stays mounted.
export const settingsCalendars = new Route({
    getParentRoute: () => settingsRoute,
    path: "/calendars",
    component: () => <SettingsCalendars />,
});

export const vendorList = new Route({
    getParentRoute: () => vendorRoute,
    path: "/",
    component: () => <VendorList />,
    validateSearch: (search: Record<string, unknown>): VendorListSchemaType => {
        return VendorListSchema.parse(search);
    },
});

export const vendorDetail = new Route({
    getParentRoute: () => vendorRoute,
    path: "/$id",
    component: () => <VendorDetail />,
});

export const friendList = new Route({
    getParentRoute: () => friendRoute,
    path: "/",
    component: () => <FriendsTable />,
});

const friendRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "friends",
    component: () => <VendorLayout />,
});

export const eventsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "events",
    component: () => <EventsLayout />,
});

const eventsListRoute = new Route({
    getParentRoute: () => eventsRoute,
    path: "/list",
    component: () => <Events />,
});

export const eventsDetailRoute = new Route({
    getParentRoute: () => eventsRoute,
    path: "/$id",
    component: () => <EventDetailPage />,
});

export const eventsJoinRoute = new Route({
    getParentRoute: () => eventsRoute,
    path: "/$id/join",
    component: () => <EventJoin />,
});

// PRTL-1087: /private-events becomes a layout route so the calendar (index
// child) and the Zoom join screen (/$id/join child) can each render fullscreen
// without one stomping the other. Previously the calendar lived directly on
// this route and didn't render an <Outlet />, so any child route never got a
// chance to mount.
export const compassEventsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "private-events",
    component: () => <CompassEventsLayout />,
});

export const compassEventsIndexRoute = new Route({
    getParentRoute: () => compassEventsRoute,
    path: "/",
    component: () => <CompassEvents />,
});

export const compassEventsJoinRoute = new Route({
    getParentRoute: () => compassEventsRoute,
    path: "/$id/join",
    component: () => <CompassEventJoin />,
});

// PRTL-852 (extended): dedicated /meetings hub. Shortcode-keyed URLs so
// they're human-friendly + the literal `series/` segment makes routing
// unambiguous (was previously /$seriesId vs /$seriesId/$instanceId, which
// TanStack Router couldn't disambiguate cleanly).
//
//   /meetings                                — index w/ today/upcoming/recent
//   /meetings/previous?page=&pageSize=        — paginated past archive
//   /meetings/series/{seriesShortCode}        — series detail
//   /meetings/{instanceShortCode}             — instance hub (auto-mounts embed)
// `showAll` is the superuser "see every meeting" toggle (default off = only the
// caller's own / created). Ignored server-side for non-superusers.
const MeetingsIndexSearchSchema = z.object({
    showAll: z.boolean().default(false),
});
export type MeetingsIndexSearchParams = z.infer<typeof MeetingsIndexSearchSchema>;

const PreviousMeetingsSearchSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(5).max(50).default(20),
    showAll: z.boolean().default(false),
});
export type PreviousMeetingsSearchParams = z.infer<typeof PreviousMeetingsSearchSchema>;

export const meetingsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "meetings",
    component: () => <MeetingsLayout />,
});

export const meetingsIndexRoute = new Route({
    getParentRoute: () => meetingsRoute,
    path: "/",
    component: () => <Meetings />,
    validateSearch: (search: Record<string, unknown>): MeetingsIndexSearchParams =>
        MeetingsIndexSearchSchema.parse(search),
});

export const meetingsPreviousRoute = new Route({
    getParentRoute: () => meetingsRoute,
    path: "previous",
    component: () => <PreviousMeetings />,
    validateSearch: (search: Record<string, unknown>): PreviousMeetingsSearchParams =>
        PreviousMeetingsSearchSchema.parse(search),
});

export const meetingsSeriesRoute = new Route({
    getParentRoute: () => meetingsRoute,
    path: "series/$seriesShortCode",
    component: () => <MeetingSeriesDetail />,
});

export const meetingsInstanceRoute = new Route({
    getParentRoute: () => meetingsRoute,
    path: "$instanceShortCode",
    component: () => <MeetingInstanceDetail />,
});

const vendorRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "vendors",
    component: () => <VendorLayout />,
});

// export const vendorCreateForm = new Route({
//     getParentRoute: () => vendorRoute,
//     path: "/new",
//     component: () => <NewVendorForm />,
// });

const rssRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "rss-feeds",
    component: () => <VendorLayout />,
});

export const rssList = new Route({
    getParentRoute: () => rssRoute,
    path: "/",
    component: () => <RssList />,
});

export const rssCategoryRoute = new Route({
    getParentRoute: () => rssRoute,
    path: "category/$categoryName",
    component: () => <FullCategory />,
});

// PRTL-981: Ask Navigator links feed articles as article:{id}; this is where
// the web portal sends them (mobile has had an article page all along).
export const rssArticleRoute = new Route({
    getParentRoute: () => rssRoute,
    path: "article/$id",
    component: () => <RssArticle />,
});

const digestRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "digest",
    component: () => <VendorLayout />,
});

export const digestList = new Route({
    getParentRoute: () => digestRoute,
    path: "/",
    component: () => <AIDigest />,
});

export const digestDetail = new Route({
    getParentRoute: () => digestRoute,
    path: "/$id",
    component: () => <AIDigestDetail />,
});

const createUserProfileRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "profile",
    component: () => <Outlet />,
});

export const userProfile = new Route({
    getParentRoute: () => createUserProfileRoute,
    path: "/$id",
    component: () => <UserProfile />,
});

export const editUserProfile = new Route({
    getParentRoute: () => createUserProfileRoute,
    path: "/edit/$id",
    component: () => <EditProfile />,
});

export const socialRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "social",
    component: () => <SocialLayout />,
});

export const socialList = new Route({
    getParentRoute: () => socialRoute,
    path: "/",
    component: () => <Social />,
});

export const socialPostsOnly = new Route({
    getParentRoute: () => socialRoute,
    path: "/posts",
    component: () => <SocialMain />,
});

export const socialPost = new Route({
    getParentRoute: () => socialRoute,
    path: "/post/$id",
    component: () => <SocialRead />,
});

export const socialProfile = new Route({
    getParentRoute: () => socialRoute,
    path: "/profile/$id",
    component: () => <Profile />,
});

export const liveRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "/live",
});

const liveListRoute = new Route({
    getParentRoute: () => liveRoute,
    path: "/",
    component: () => <Live />,
});

// PRTL-507: keyed by the ZoomMeeting row id, not the Zoom meeting number.
export const liveJoinRoute = new Route({
    getParentRoute: () => liveRoute,
    path: "/$id/join",
    component: () => <LiveJoin />,
});

const chartsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "visualizations",
    component: () => <Outlet />,
});

export const chartsListRoute = new Route({
    getParentRoute: () => chartsRoute,
    path: "/",
    component: () => <ChartsListScreen />,
    validateSearch: (search: Record<string, unknown>): ChartsListSchemaType => {
        return ChartsListSchema.parse(search);
    },
});

export const chartNewRoute = new Route({
    getParentRoute: () => chartsRoute,
    path: "/new",
    component: () => <CreateChartScreen />,
});

export const chartDetailRoute = new Route({
    getParentRoute: () => chartsRoute,
    path: "/$id",
    component: () => <Outlet />,
});

export const chartViewRoute = new Route({
    getParentRoute: () => chartDetailRoute,
    path: "/",
    component: () => <ChartDetailScreen />,
});

export const chartEditRoute = new Route({
    getParentRoute: () => chartDetailRoute,
    path: "/edit",
    component: () => <ChartDetailScreen />,
});

export const chartVersionRoute = new Route({
    getParentRoute: () => chartDetailRoute,
    path: "/$versionId",
    component: () => <ChartDetailScreen />,
});

export const chartVersionEditRoute = new Route({
    getParentRoute: () => chartVersionRoute,
    path: "/edit",
    component: () => <ChartDetailScreen />,
});

/**
 * Messages
 */

export const messagesRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "messages",
    component: () => <Outlet />,
});

export const messagesHomeRoute = new Route({
    getParentRoute: () => messagesRoute,
    path: "/",
    component: () => <Messages />,
});

export const messagesUserRoute = new Route({
    getParentRoute: () => messagesRoute,
    path: "/$id",
    component: () => <Messages />,
});

/**
 * Auth
 */
export const loginRoute = new Route({
    getParentRoute: () => rootRoute,
    path: "login",
    component: () => <Login />,
});

const logoutRoute = new Route({
    getParentRoute: () => rootRoute,
    path: "logout",
    component: () => <Logout />,
});

// PRTL-1348: masquerade exchange landing (code arrives in the URL fragment).
const masqueradeRoute = new Route({
    getParentRoute: () => rootRoute,
    path: "masquerade",
    component: () => <Masquerade />,
});

const forgotPasswordRoute = new Route({
    getParentRoute: () => rootRoute,
    path: "forgot-password",
    component: () => <ForgotPassword />,
});

export const resetPasswordRoute = new Route({
    getParentRoute: () => rootRoute,
    path: "reset-password",
    component: () => <ResetPassword />,
    validateSearch: (search) => {
        if (typeof search.token !== "string") {
            throw new Error("Missing reset token");
        }
        return { token: search.token };
    },
});

const benchmarkRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "benchmark",
    component: () => <VendorLayout />,
});

export const benchmarkList = new Route({
    getParentRoute: () => benchmarkRoute,
    path: "/",
    component: () => <BenchMark />,
});

const budgetRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "budget",
    component: () => <VendorLayout />,
});

export const budgetList = new Route({
    getParentRoute: () => budgetRoute,
    path: "/",
    component: () => <Budget />,
});

export const resolveNotificationRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "n/$token",
    component: () => <ResolveNotification />,
});

export const notificationsRoute = new Route({
    getParentRoute: () => portalRoute,
    path: "notifications",
    component: () => <NotificationsPage />,
});

const notFoundRoute = new Route({
    getParentRoute: () => rootRoute,
    path: "*",
    component: () => <NotFound />,
});

const routeTree = rootRoute.addChildren([
    portalRoute.addChildren([
        // homeRoute,
        favoritesRoute.addChildren([
            favoritesIndexRoute,
            favoritesPrivateRoute,
            favoritesPublicRoute,
        ]),
        trendingRoute,
        resourceCategoryRoute,
        resourcePostRoute,
        authorPostsRoute,
        discussionsRoute.addChildren([
            discussionsList,
            // discussionsNewRoute,
            discussionsReadRoute,
        ]),
        documentsRoute,
        settingsRoute.addChildren([settingsSecurity, settingsNotifications, settingsCalendars]),
        socialRoute.addChildren([socialList, socialPostsOnly, socialPost, socialProfile]),
        friendRoute.addChildren([friendList]),
        vendorRoute.addChildren([vendorList, vendorDetail]),
        digestRoute.addChildren([digestList, digestDetail]),
        benchmarkRoute.addChildren([benchmarkList]),
        budgetRoute.addChildren([budgetList]),
        rssRoute.addChildren([rssList, rssCategoryRoute, rssArticleRoute]),
        createUserProfileRoute.addChildren([userProfile, editUserProfile]),
        messagesRoute.addChildren([messagesHomeRoute, messagesUserRoute]),
        resolveNotificationRoute,
        notificationsRoute,
        eventsRoute.addChildren([eventsListRoute, eventsDetailRoute, eventsJoinRoute]),
        chartsRoute.addChildren([
            chartsListRoute,
            chartNewRoute,
            chartDetailRoute.addChildren([
                chartViewRoute,
                chartEditRoute,
                chartVersionRoute.addChildren([chartVersionEditRoute]),
            ]),
        ]),
        compassEventsRoute.addChildren([compassEventsIndexRoute, compassEventsJoinRoute]),
        meetingsRoute.addChildren([
            meetingsIndexRoute,
            meetingsPreviousRoute,
            meetingsSeriesRoute,
            meetingsInstanceRoute,
        ]),
        liveRoute.addChildren([liveListRoute, liveJoinRoute]),
    ]),
    loginRoute,
    logoutRoute,
    masqueradeRoute,
    resetPasswordRoute,
    forgotPasswordRoute,
    notFoundRoute,
]);

export const router = new ReactRouter({
    routeTree,
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}
